import React from "react";
import SiteIcon from "./SiteIcon";
import RichDescription from "./RichDescription";

export default function SiteDetails({ site }) {
    if (!site) {
        return (
            <div className="flex h-full items-center justify-center text-sm text-slate-400 dark:text-slate-500">
                Select a site or folder on the left to see a detailed description.
            </div>
        );
    }

    const isFolder = site.type === "folder";
    const hasDescription =
        typeof site.description === "string" && site.description.trim().length > 0;
    const hasShortDescription =
        typeof site.shortDescription === "string" &&
        site.shortDescription.trim().length > 0;

    return (
        <div className="space-y-4">
            <div className="flex items-start gap-4">
                <SiteIcon
                    siteUrl={isFolder ? undefined : site.siteUrl}
                    logoUrl={site.logoUrl}
                    name={site.name}
                    size="md"
                    className="rounded-3xl"
                    isFolder={isFolder}
                />

                <div className="space-y-2 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                        <h2 className="text-lg md:text-xl font-semibold tracking-tight text-slate-900 dark:text-slate-50 break-words">
                            {isFolder ? `📁 ${site.name}` : site.name}
                        </h2>

                        <span
                            className={
                                "inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium " +
                                (isFolder
                                    ? "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
                                    : "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300")
                            }
                        >
                            {isFolder ? "Folder" : "Site"}
                        </span>

                        {typeof site.isPersonal === "boolean" && (
                            <span
                                className={
                                    "inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium " +
                                    (site.isPersonal
                                        ? "bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300"
                                        : "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300")
                                }
                            >
                                {site.isPersonal ? "Personal" : "Public"}
                            </span>
                        )}
                    </div>

                    <p className="text-xs md:text-sm text-slate-500 dark:text-slate-300">
                        {hasShortDescription
                            ? site.shortDescription
                            : isFolder
                                ? "This folder contains nested folders and project items."
                                : "No short description available."}
                    </p>

                    {!isFolder && site.siteUrl && (
                        <a
                            href={site.siteUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 text-xs font-medium text-sky-600 hover:text-sky-700 hover:underline dark:text-sky-400 dark:hover:text-sky-300"
                        >
                            Open website
                            <span aria-hidden="true">↗</span>
                        </a>
                    )}
                </div>
            </div>

            <div className="h-px w-full bg-gradient-to-r from-transparent via-slate-200 to-transparent dark:via-slate-700" />

            {hasDescription ? (
                <RichDescription text={site.description} />
            ) : (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/70 px-4 py-5 text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-400">
                    {isFolder
                        ? "This folder has no description yet."
                        : "Detailed description is not available yet."}
                </div>
            )}
        </div>
    );
}